import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Confetti from '../components/Confetti';
import MilestoneCelebration from '../components/MilestoneCelebration';
import MotivationalMessage from '../components/MotivationalMessage';

const API_BASE = '/api';

// Category display config
const CATEGORY_CONFIG = {
  database: {
    label: 'DATABASE',
    bgColor: 'bg-green-50',
    headerBg: 'bg-green-600',
    headerText: 'text-white',
    borderColor: 'border-green-200',
    accentColor: 'text-green-700',
    buttonBg: 'bg-green-600 hover:bg-green-700',
    progressBg: 'bg-green-500',
    icon: '📊',
  },
  grassroots: {
    label: 'GRASSROOTS',
    bgColor: 'bg-yellow-50',
    headerBg: 'bg-yellow-500',
    headerText: 'text-white',
    borderColor: 'border-yellow-200',
    accentColor: 'text-yellow-700',
    buttonBg: 'bg-yellow-500 hover:bg-yellow-600',
    progressBg: 'bg-yellow-500',
    icon: '🌱',
  },
  strategic_partners: {
    label: 'STRATEGIC PARTNERS',
    bgColor: 'bg-blue-50',
    headerBg: 'bg-blue-700',
    headerText: 'text-white',
    borderColor: 'border-blue-200',
    accentColor: 'text-blue-700',
    buttonBg: 'bg-blue-600 hover:bg-blue-700',
    progressBg: 'bg-blue-500',
    icon: '🤝',
  },
};

// Helper to format date as YYYY-MM-DD in local timezone
function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Helper to shift date by N days
function shiftDate(dateStr, days) {
  const d = new Date(dateStr + 'T12:00:00'); // noon to avoid DST issues
  d.setDate(d.getDate() + days);
  return toLocalDateString(d);
}

// Validate date string format YYYY-MM-DD and that it's a real date
function isValidDate(dateStr) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const d = new Date(dateStr + 'T12:00:00');
  return !isNaN(d.getTime()) && toLocalDateString(d) === dateStr;
}

export default function DailyViewPage() {
  const { user, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [habits, setHabits] = useState([]);
  const [logs, setLogs] = useState({});
  const [targets, setTargets] = useState({});
  const [streak, setStreak] = useState({ current_streak: 0, best_streak: 0 });
  const [dailyScore, setDailyScore] = useState({ total_xp: 0, max_possible_xp: 0 });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null); // habit_id being updated
  const [showConfetti, setShowConfetti] = useState(false);
  const prevAllMetRef = useRef(false);
  const confettiShownForDateRef = useRef(null);

  // Debouncing for rapid clicks
  const [pendingTally, setPendingTally] = useState(null); // { habitId, action }
  const tallyTimeoutRef = useRef(null);

  const todayStr = toLocalDateString(new Date());

  // Initialize date from URL query param, falling back to today
  const dateParam = searchParams.get('date');
  const initialDate = (dateParam && isValidDate(dateParam) && dateParam <= todayStr)
    ? dateParam
    : todayStr;
  const [selectedDate, setSelectedDate] = useState(initialDate);

  const isToday = selectedDate === todayStr;

  // Compute display info for selected date
  const selectedDateObj = new Date(selectedDate + 'T12:00:00');
  const isSunday = selectedDateObj.getDay() === 0;
  const dateDisplay = selectedDateObj.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Can't go to future dates
  const canGoForward = selectedDate < todayStr;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [habitsRes, logsRes, targetsRes, streakRes, scoreRes] = await Promise.all([
        fetch(`${API_BASE}/habits`, { headers }),
        fetch(`${API_BASE}/logs?date=${selectedDate}`, { headers }),
        fetch(`${API_BASE}/habits/targets`, { headers }),
        fetch(`${API_BASE}/scores/streak`, { headers }),
        fetch(`${API_BASE}/scores/daily?date=${selectedDate}`, { headers }),
      ]);

      const habitsData = await habitsRes.json();
      const logsData = await logsRes.json();
      const targetsData = await targetsRes.json();
      const streakData = await streakRes.json();
      const scoreData = await scoreRes.json();

      // Filter to only daily habits
      const dailyHabits = (habitsData.habits || []).filter(h => h.frequency === 'daily');
      setHabits(dailyHabits);

      // Index logs by habit_id
      const logsMap = {};
      (logsData.logs || []).forEach(l => {
        logsMap[l.habit_id] = l;
      });
      setLogs(logsMap);

      // Index targets by habit_id
      const targetsMap = {};
      (targetsData.targets || []).forEach(t => {
        targetsMap[t.habit_id] = t.custom_target;
      });
      setTargets(targetsMap);

      setStreak(streakData.streak || { current_streak: 0, best_streak: 0 });
      setDailyScore(scoreData.score || { total_xp: 0, max_possible_xp: 0 });
    } catch (err) {
      console.error('Error loading daily view:', err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (tallyTimeoutRef.current) {
        clearTimeout(tallyTimeoutRef.current);
      }
    };
  }, []);

  const handleTally = async (habitId, action) => {
    if (!isToday) return; // Safety: don't allow edits for past dates

    // Clear any existing timeout
    if (tallyTimeoutRef.current) {
      clearTimeout(tallyTimeoutRef.current);
    }

    // Update pending state (for immediate UI feedback)
    setPendingTally({ habitId, action });

    // Debounce: wait 300ms before sending the request
    tallyTimeoutRef.current = setTimeout(async () => {
      setUpdating(habitId);
      try {
        const res = await fetch(`${API_BASE}/logs/tally`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ habit_id: habitId, action, date: selectedDate }),
        });
        const data = await res.json();
        if (res.ok && data.log) {
          // Update local state for instant feedback
          setLogs(prev => ({ ...prev, [habitId]: data.log }));
          // Refetch score and streak
          const headers = { Authorization: `Bearer ${token}` };
          const [scoreRes, streakRes] = await Promise.all([
            fetch(`${API_BASE}/scores/daily?date=${selectedDate}`, { headers }),
            fetch(`${API_BASE}/scores/streak`, { headers }),
          ]);
          const scoreData = await scoreRes.json();
          const streakData = await streakRes.json();
          setDailyScore(scoreData.score || { total_xp: 0, max_possible_xp: 0 });
          setStreak(streakData.streak || { current_streak: 0, best_streak: 0 });
        } else {
          console.error('Tally update failed:', data.error);
        }
      } catch (err) {
        console.error('Error updating tally:', err);
      } finally {
        setUpdating(null);
        setPendingTally(null);
      }
    }, 300);
  };

  // Sync URL with selected date
  useEffect(() => {
    const currentParam = searchParams.get('date');
    if (selectedDate === todayStr) {
      // Remove date param when on today
      if (currentParam) {
        setSearchParams({}, { replace: true });
      }
    } else {
      if (currentParam !== selectedDate) {
        setSearchParams({ date: selectedDate }, { replace: true });
      }
    }
  }, [selectedDate, todayStr, searchParams, setSearchParams]);

  const navigateDate = (direction) => {
    if (direction === 'prev') {
      setSelectedDate(prev => shiftDate(prev, -1));
    } else if (direction === 'next' && canGoForward) {
      setSelectedDate(prev => shiftDate(prev, 1));
    }
  };

  const goToToday = () => {
    setSelectedDate(todayStr);
  };

  // Group daily habits by category
  const groupedHabits = {};
  habits.forEach(habit => {
    if (!groupedHabits[habit.category]) {
      groupedHabits[habit.category] = [];
    }
    groupedHabits[habit.category].push(habit);
  });

  // Calculate progress percentage
  const progressPercent = dailyScore.max_possible_xp > 0
    ? Math.round((dailyScore.total_xp / dailyScore.max_possible_xp) * 100)
    : 0;

  // Check if all daily habits meet targets
  const allTargetsMet = habits.length > 0 && habits.every(habit => {
    const log = logs[habit.id];
    const tallyCount = log ? log.tally_count : 0;
    const target = targets[habit.id] || habit.default_min_target;
    return tallyCount >= target;
  });

  // Trigger confetti when transitioning to all targets met (not on initial load)
  useEffect(() => {
    if (!loading && isToday && habits.length > 0) {
      if (allTargetsMet && !prevAllMetRef.current && confettiShownForDateRef.current !== selectedDate) {
        setShowConfetti(true);
        confettiShownForDateRef.current = selectedDate;
      }
      prevAllMetRef.current = allTargetsMet;
    }
  }, [allTargetsMet, loading, isToday, habits.length, selectedDate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Empty state when no habits are loaded
  if (habits.length === 0) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <div className="text-6xl mb-4">📋</div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">No Habits Found</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Unable to load your daily habits. Please contact support if this issue persists.
        </p>
      </div>
    );
  }

  // Category order
  const categoryOrder = ['database', 'grassroots', 'strategic_partners'];

  // Read-only mode: past dates or Sunday
  const isReadOnly = !isToday || isSunday;

  const handleConfettiComplete = () => {
    setShowConfetti(false);
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="daily-view-page">
      {/* Confetti Celebration */}
      <Confetti active={showConfetti} onComplete={handleConfettiComplete} />

      {/* Streak Milestone Celebration */}
      <MilestoneCelebration streak={streak} />

      {/* Date Navigation Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateDate('prev')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors text-gray-600 dark:text-gray-300"
            data-testid="date-nav-prev"
            aria-label="Previous day"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white" data-testid="today-date">{dateDisplay}</h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {isToday ? 'Daily Habit Tracking' : 'Viewing Past Date (Read-Only)'}
            </p>
          </div>

          <button
            onClick={() => navigateDate('next')}
            disabled={!canGoForward}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors text-gray-600 dark:text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="date-nav-next"
            aria-label="Next day"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Return to Today button when viewing past date */}
        {!isToday && (
          <div className="mt-3 text-center">
            <button
              onClick={goToToday}
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-800 transition-colors text-sm font-medium"
              data-testid="date-nav-today"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Back to Today
            </button>
          </div>
        )}
      </div>

      {/* Past Date Read-Only Banner */}
      {!isToday && !isSunday && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-center" data-testid="past-date-banner">
          <p className="text-sm text-amber-700 font-medium">
            {'🔒'} You are viewing a past date. Habits are read-only.
          </p>
        </div>
      )}

      {/* Sunday Rest Day Banner */}
      {isSunday && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-center" data-testid="sunday-rest-banner">
          <span className="text-2xl mb-2 block">{'😴'}</span>
          <h2 className="text-lg font-bold text-blue-800">Rest Day</h2>
          <p className="text-sm text-blue-600 mt-1">Sunday is always a rest day. Habit tracking resumes tomorrow!</p>
        </div>
      )}

      {/* Motivational Message - only show for today */}
      {isToday && !isSunday && (
        <MotivationalMessage
          progressPercent={progressPercent}
          streak={streak}
          userId={user?.id}
          dateStr={selectedDate}
        />
      )}

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {/* Daily XP */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Daily XP</div>
          <div className="text-2xl font-bold text-primary" data-testid="daily-xp">
            {dailyScore.total_xp} <span className="text-sm font-normal text-gray-400">/ {dailyScore.max_possible_xp}</span>
          </div>
        </div>

        {/* Progress */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Progress</div>
          <div className="text-2xl font-bold text-primary" data-testid="daily-progress-percent">{progressPercent}%</div>
        </div>

        {/* Streak */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm col-span-2 md:col-span-1">
          <div className="text-xs text-gray-500 uppercase font-medium mb-1">Current Streak</div>
          <div className="flex items-center gap-2">
            <span className="text-2xl" data-testid="streak-fire-icon">{'🔥'}</span>
            <span className="text-2xl font-bold text-orange-500" data-testid="streak-count">{streak.current_streak}</span>
            <span className="text-sm text-gray-400">days</span>
          </div>
        </div>
      </div>

      {/* Daily Progress Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm mb-6" data-testid="daily-progress-bar">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Daily Progress</span>
          <span className="text-sm text-gray-500">{dailyScore.total_xp} / {dailyScore.max_possible_xp} XP ({progressPercent}%)</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className="bg-primary rounded-full h-3 transition-all duration-500"
            style={{ width: `${Math.min(progressPercent, 100)}%` }}
          ></div>
        </div>
      </div>

      {/* Habit Categories */}
      {categoryOrder.map(category => {
        const config = CATEGORY_CONFIG[category];
        const categoryHabits = groupedHabits[category];
        if (!categoryHabits || categoryHabits.length === 0) return null;

        return (
          <div
            key={category}
            className={`rounded-lg border ${config.borderColor} ${config.bgColor} mb-6 overflow-hidden shadow-sm`}
            data-testid={`category-${category}`}
          >
            {/* Category Header */}
            <div className={`${config.headerBg} ${config.headerText} px-4 py-3 flex items-center gap-2`} data-testid={`category-header-${category}`}>
              <span className="text-lg">{config.icon}</span>
              <h2 className="font-bold text-sm tracking-wider">{config.label}</h2>
            </div>

            {/* Habit Cards */}
            <div className="p-3 space-y-3">
              {categoryHabits.map(habit => {
                const log = logs[habit.id];
                const tallyCount = log ? log.tally_count : 0;
                const target = targets[habit.id] || habit.default_min_target;
                const xpEarned = log ? log.xp_earned : 0;
                const isComplete = tallyCount >= target;
                const isUpdating = updating === habit.id;

                return (
                  <div
                    key={habit.id}
                    className={`bg-white rounded-lg p-4 border ${isComplete ? 'border-green-300 ring-1 ring-green-200' : 'border-gray-200'} transition-all`}
                    data-testid={`habit-card-${habit.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {isComplete && (
                            <span className="text-green-500 text-lg" data-testid={`habit-complete-${habit.id}`}>{'✅'}</span>
                          )}
                          <h3 className="font-medium text-gray-800 text-sm break-words" data-testid={`habit-name-${habit.id}`}>
                            {habit.name}
                          </h3>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className={`text-xs ${config.accentColor} font-medium`}>
                            {xpEarned} XP earned
                          </span>
                          <span className="text-xs text-gray-400">
                            ({habit.xp_per_unit} XP each)
                          </span>
                        </div>
                      </div>

                      {/* Tally Counter */}
                      <div className="flex items-center gap-2 ml-4" data-testid={`tally-controls-${habit.id}`}>
                        <button
                          onClick={() => handleTally(habit.id, 'decrement')}
                          disabled={isReadOnly || isUpdating || tallyCount === 0}
                          className="w-9 h-9 flex items-center justify-center rounded-full border-2 border-gray-300 text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-bold text-lg"
                          data-testid={`tally-decrement-${habit.id}`}
                          aria-label={`Decrement ${habit.name}`}
                        >
                          -
                        </button>
                        <div className="text-center min-w-[60px]">
                          <span className="text-xl font-bold text-gray-800" data-testid={`tally-count-${habit.id}`}>
                            {tallyCount}
                          </span>
                          <span className="text-sm text-gray-400"> / {target}</span>
                        </div>
                        <button
                          onClick={() => handleTally(habit.id, 'increment')}
                          disabled={isReadOnly || isUpdating}
                          className={`w-9 h-9 flex items-center justify-center rounded-full text-white ${isReadOnly ? 'bg-gray-400' : config.buttonBg} disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold text-lg shadow-sm`}
                          data-testid={`tally-increment-${habit.id}`}
                          aria-label={`Increment ${habit.name}`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
